import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2, Loader2, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField, isoDay } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

const BLANK = { requester_name: "", organisation: "", email: "", phone: "", space_code: "",
  booking_date: "", start_time: "10:00", end_time: "13:00", attendees: 0, purpose: "",
  notes: "", terms_accepted: false };

export default function HallHire() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/hall-hire/public/config");
    setCfg(res.data);
    setForm((f) => ({ ...f, booking_date: res.data.earliest_date || "" }));
  }, []);

  useEffect(() => { load().catch(() => setCfg({ enabled: false })); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/hall-hire/public/requests", form);
      setDone(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!cfg) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!cfg.enabled) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-6">
        <div className="max-w-md text-center" data-testid="hall-hire-disabled">
          <h1 className="font-display text-2xl font-bold text-foreground">Hall hire</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">
            {cfg.message || "Online hire requests are closed at the moment."}
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-6">
        <div className="max-w-lg text-center" data-testid="hall-hire-submitted">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground">
            Request received
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{done.message}</p>
          <p className="mt-3 font-mono text-[12px] text-primary" data-testid="hall-hire-reference">
            Reference {done.request_ref}
          </p>
        </div>
      </div>
    );
  }

  const chosen = cfg.rooms.find((r) => r.code === form.space_code);
  const ready = form.requester_name.trim().length > 1 && form.email.includes("@")
    && form.purpose.trim().length > 2 && form.booking_date && form.terms_accepted;

  return (
    <div className="min-h-screen bg-background px-5 py-10" data-testid="hall-hire-page">
      <div className="mx-auto max-w-5xl">
        <div className="max-w-2xl">
          <div className="kicker">{cfg.organisation} · Facility hire</div>
          <h1 className="mt-1 font-display text-3xl font-bold text-foreground sm:text-4xl">
            Request one of our halls or rooms
          </h1>
          <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">{cfg.intro}</p>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="Your name">
                <Input value={form.requester_name} data-testid="hire-name-input"
                       onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="Organisation or group (optional)">
                <Input value={form.organisation} data-testid="hire-org-input"
                       onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="Email">
                <Input type="email" value={form.email} data-testid="hire-email-input"
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="Phone (optional)">
                <Input value={form.phone} data-testid="hire-phone-input"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="Which space">
                <Select value={form.space_code || "any"}
                        onValueChange={(v) => setForm({ ...form, space_code: v === "any" ? "" : v })}>
                  <SelectTrigger data-testid="hire-room-select" className="bg-muted/40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any suitable space</SelectItem>
                    {cfg.rooms.map((r) => (
                      <SelectItem key={r.code} value={r.code}>
                        {r.name}{r.room_number ? ` (${r.room_number})` : ""} · seats {r.capacity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Expected number of people">
                <Input type="number" value={form.attendees} data-testid="hire-attendees-input"
                       onChange={(e) => setForm({ ...form, attendees: Number(e.target.value) })}
                       className="bg-muted/40 font-mono" />
              </F>
              <F label="Date" hint={`Earliest ${cfg.earliest_date}, latest ${cfg.latest_date}`}>
                <DateField value={form.booking_date} testId="hire-date-input"
                           onChange={(v) => setForm({ ...form, booking_date: v })}
                           clearable={false}
                           disabled={(d) => isoDay(d) < cfg.earliest_date
                             || isoDay(d) > cfg.latest_date} />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="From">
                  <Input type="time" value={form.start_time} data-testid="hire-start-input"
                         onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                         className="bg-muted/40 font-mono" />
                </F>
                <F label="Until">
                  <Input type="time" value={form.end_time} data-testid="hire-end-input"
                         onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                         className="bg-muted/40 font-mono" />
                </F>
              </div>
              <div className="sm:col-span-2">
                <F label="What is the space for">
                  <Input value={form.purpose} data-testid="hire-purpose-input"
                         placeholder="For example: weekly yoga class, community meeting, wedding reception"
                         onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                         className="bg-muted/40" />
                </F>
              </div>
              <div className="sm:col-span-2">
                <F label="Anything else we should know (optional)">
                  <Textarea rows={3} value={form.notes} data-testid="hire-notes-input"
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="bg-muted/40 text-[12.5px]" />
                </F>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-muted/30 p-3.5">
              <p className="text-[11.5px] leading-relaxed text-muted-foreground">{cfg.terms}</p>
              <label className="mt-3 flex items-start gap-2.5 text-[12px] text-foreground">
                <Checkbox checked={form.terms_accepted} data-testid="hire-terms-checkbox"
                          onCheckedChange={(v) => setForm({ ...form, terms_accepted: Boolean(v) })} />
                I have read and accept the conditions of hire.
              </label>
            </div>

            <Button disabled={!ready || busy} onClick={submit} data-testid="hire-submit-button"
                    className="mt-4 h-10 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <CalendarCheck className="mr-2 h-4 w-4" />}
              Send the request
            </Button>
            <p className="mt-2 text-center text-[10.5px] text-muted-foreground">
              A request is not a confirmed booking. The facilities team will reply with
              availability and the hire charge.
            </p>
          </div>

          <div className="space-y-3">
            {cfg.rooms.map((r) => (
              <div key={r.code} data-testid={`hire-room-card-${r.code}`}
                   className={`rounded-xl border p-3.5 transition-colors duration-150 ${
                     chosen?.code === r.code ? "border-primary bg-primary/[0.06]" : "border-border bg-card"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[13px] font-semibold text-foreground">{r.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">
                      {r.room_number || r.code} · {r.space_type}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                    <Users className="h-3 w-3" /> {r.capacity}
                  </span>
                </div>
                {cfg.show_rates && (
                  <div className="mt-2 font-mono text-[11px] text-foreground/80">
                    {money(r.hourly_rate, r.currency)} / hour ·{" "}
                    {money(r.day_rate, r.currency)} / day
                  </div>
                )}
                {r.notes && (
                  <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{r.notes}</p>
                )}
                {r.policies && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`hire-policies-${r.code}`}>
                    {[["Pets", r.policies.pet_friendly], ["Smoking", r.policies.smoking_policy],
                      ["Food", r.policies.food_allowed], ["Fireplace", r.policies.fireplace_allowed],
                      ["Children", r.policies.kids_friendly],
                      ["Play area", r.policies.kids_play_area],
                      ["Parking", r.policies.parking_available]]
                      .filter(([, v]) => v).map(([label, value]) => (
                        <span key={label}
                              className={`rounded-full border px-2 py-0.5 text-[10px] ${
                                ["allowed", "available", "included"].includes(value)
                                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                                  : "border-border bg-muted text-muted-foreground"}`}>
                          {label}: {String(value).replace(/_/g, " ")}
                        </span>
                      ))}
                    {r.policies.parking_available === "available"
                      && r.policies.parking_included === "chargeable" && (
                      <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                        Parking {money(r.policies.parking_charge, r.currency)}
                        {r.policies.parking_spaces ? ` · ${r.policies.parking_spaces} spaces` : ""}
                      </span>
                    )}
                  </div>
                )}
                {r.policies?.policy_notes && (
                  <p className="mt-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
                    {r.policies.policy_notes}
                  </p>
                )}
              </div>
            ))}
            {(cfg.contact_email || cfg.contact_phone) && (
              <div className="rounded-xl border border-border bg-card p-3.5 text-[11.5px] text-muted-foreground">
                <div className="flex items-center gap-1.5 text-foreground">
                  <MapPin className="h-3.5 w-3.5 text-primary" /> Prefer to talk to someone?
                </div>
                <div className="mt-1 font-mono text-[11px]">
                  {cfg.contact_email}{cfg.contact_email && cfg.contact_phone ? " · " : ""}
                  {cfg.contact_phone}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
